import { useEffect, useState } from 'react';

import { Box, Button, Header, Link, Pagination, SpaceBetween, Table, TextFilter } from '@amzn/awsui-components-react';
import { useRepositoryContext } from '../repository-request/RepositoryContext';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { CollectionPreferences } from '@amzn/awsui-components-react';
import { DefaultOpenSourceRepoManagerAlias } from '@amzn/code-defender-common-type-script';
import { AddRepoModal } from './AddRepoModal';
import { DeleteRepoModal } from './DeleteRepoModal';
import { PhoneToolHyperlink } from '../phone-tool-hyperlink';
import { User } from '../../core/user';
import { getRepos } from '../../helpers/repos';
import { getDelegationsFromRoles } from '../../helpers/delegation';
import { isAdmin } from '../../helpers/isAdmin';
import { renderedRepoType, RepoType } from '../../core/constants';
import { isTopRopeFeatureOn } from '../../helpers/isTopRopeFlagOn';
import { isBypassesPhase1On } from '../../helpers/isBypassesPhase1On';
import { getDefaultLocale } from '../../helpers/getDefaultLocale';

// From https://stackoverflow.com/questions/3177836
function timeAgo(input) {
  const date = input instanceof Date ? input : new Date(input);
  const formatter = new Intl.RelativeTimeFormat(getDefaultLocale());
  const ranges = {
    years: 3600 * 24 * 365,
    months: 3600 * 24 * 30,
    weeks: 3600 * 24 * 7,
    days: 3600 * 24,
    hours: 3600,
    minutes: 60,
    seconds: 1,
  };
  const secondsElapsed = (date.getTime() - Date.now()) / 1000;
  for (let key in ranges) {
    if (ranges[key] < Math.abs(secondsElapsed)) {
      const delta = secondsElapsed / ranges[key];
      return formatter.format(Math.round(delta), key);
    }
  }

  return 'Just now';
}

// Get the rendered type for the repo based on the current user and the owner
// of the repo. The rules are as follows.
// repo.repoType == RepoType.OpenSource => "Open Source"
// repo.managerAlias == currUser.username => "Org-Specific" though "Mine" is better
// currUser.delegations.has(repo.managerAlias) => "Delegated"
// repo.managerAlias == currUser.chain[-1] => "Org-Specific"
// currUser.delegations.managerChain.has(repo.managerAlias ) => "Delegated, Inherited"
// otherwise => "Inherited"
export const renderedRepoOrigin = (repo) => {
  if (repo?.repoType === RepoType.OpenSource) {
    return 'Open Source';
  }

  const user = new User();
  if (!repo) {
    return '';
  }

  if (repo.managerAlias === user.userId) {
    return 'Org-Specific';
  }

  if (new Set(getDelegationsFromRoles().map((d) => d.managerAlias)).has(repo.managerAlias)) {
    return 'Delegated';
  }

  if (user.chain?.at(-1) === repo.managerAlias) {
    return 'Org-Specific';
  }

  if (new Set(getDelegationsFromRoles().flatMap((d) => d.managerChain)).has(repo.managerAlias)) {
    return 'Delegated, Inherited';
  }

  return 'Inherited';
};

// Component to display a table of allowed repos and allow for searching and
// pagination. The parameters taken by this component are
// - adminView: boolean => Indicates whether or not the administrative
//            functionality of this component is enabled
export const RepoSearchTable = ({ adminView, user }) => {
  const { approvedReposTrigger } = useRepositoryContext();

  const [allRepos, setAllRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [addModalIsVisible, setAddModalIsVisible] = useState(false);
  const [deleteModalIsVisible, setDeleteModalIsVisible] = useState(false);

  // Collection preferences defaults
  const [preferences, setPreferences] = useState({
    pageSize: 20,
    visibleContent: ['url', 'managerAlias', 'creator', 'repoType', 'repoOrigin', 'dateAdded'],
    wrapLines: false,
    stripedRows: true,
    contentDensity: 'comfortable',
  });

  // Define column definitions for the table
  const columnDefinitions = [
    {
      id: 'url',
      header: 'Url',
      cell: (item) => (
        <Link href={item.url} target="_blank">
          {item.url}
        </Link>
      ),
      sortingField: 'url',
      isRowHeader: true,
    },
    {
      id: 'managerAlias',
      header: 'Manager',
      cell: (item) => {
        // Display manager if it exists and is not a placeholder value
        if (item.managerAlias && item.managerAlias !== DefaultOpenSourceRepoManagerAlias) {
          return <PhoneToolHyperlink alias={item.managerAlias} />;
        }
        return '';
      },
      sortingField: 'managerAlias',
    },
    {
      id: 'creator',
      header: 'Creator',
      cell: (item) => {
        // Display creator if it exists and is not empty
        if (item.creator && item.creator !== '') {
          return <PhoneToolHyperlink alias={item.creator} />;
        }
        return '';
      },
      sortingField: 'creator',
    },
    {
      id: 'repoType',
      header: 'Type',
      cell: (item) => <span title={renderedRepoType(item)}>{renderedRepoType(item)}</span>,
      sortingField: 'repoType',
    },
    {
      id: 'repoOrigin',
      header: 'Origin',
      cell: renderedRepoOrigin,
    },
    {
      id: 'dateAdded',
      header: 'Added',
      cell: (item) => timeAgo(item.dateAdded),
      sortingField: 'dateAdded',
      sortingComparator: (a, b) => {
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateA.getTime() - dateB.getTime();
      },
    },
  ];

  // Table content display options for preferences
  const contentDisplayOptions = [
    {
      id: 'url',
      label: 'URL',
      alwaysVisible: true,
    },
    {
      id: 'managerAlias',
      label: 'Manager',
    },
    {
      id: 'creator',
      label: 'Creator',
    },
    {
      id: 'repoType',
      label: 'Type',
    },
    {
      id: 'repoOrigin',
      label: 'Origin',
    },
    {
      id: 'dateAdded',
      label: 'Added Date',
    },
  ];

  // Pagination options for preferences
  const pageSizeOptions = [
    { value: 10, label: '10 repositories' },
    { value: 20, label: '20 repositories' },
    { value: 50, label: '50 repositories' },
    { value: 100, label: '100 repositories' },
  ];

  // Handle preferences change
  const onPreferencesChange = (event) => {
    setPreferences(event.detail);
  };

  // Setup collection hook for client-side filtering, sorting, and pagination
  const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(
    allRepos,
    {
      filtering: {
        empty: (
          <Box textAlign="center" color="inherit">
            <b>No repositories found</b>
            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
              No repositories match the filters
            </Box>
          </Box>
        ),
        noMatch: (
          <Box textAlign="center" color="inherit">
            <b>No matches</b>
            <Box padding={{ bottom: 's' }} variant="p" color="inherit">
              No repositories match the filter criteria
            </Box>
            <Button
              onClick={() => {
                actions.setFiltering('');
              }}
            >
              Clear filter
            </Button>
          </Box>
        ),
        filteringFunction: (item, filteringText) => {
          // Custom filtering function that prioritizes URL matching
          const filterText = filteringText.toLowerCase();
          if (!filterText) {
            return true;
          }

          // Check URL (primary search target)
          if (item.url && item.url.toLowerCase().includes(filterText)) {
            return true;
          }

          // Secondary matches in other fields
          if (item.managerAlias && item.managerAlias.toLowerCase().includes(filterText)) {
            return true;
          }

          if (item.creator && item.creator.toLowerCase().includes(filterText)) {
            return true;
          }

          if (item.reason && item.reason.toLowerCase().includes(filterText)) {
            return true;
          }

          const type = renderedRepoType(item);
          if (type && type.toLowerCase().includes(filterText)) {
            return true;
          }

          const origin = renderedRepoOrigin(item);
          if (origin && origin.toLowerCase().includes(filterText)) {
            return true;
          }

          return false;
        },
      },
      pagination: { pageSize: preferences.pageSize },
      sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
      selection: {},
    },
  );

  // Effect to load all repositories
  useEffect(() => {
    const fetchRepos = async () => {
      setLoading(true);
      try {
        const response = await getRepos();
        setAllRepos(response.repos || []);
      } catch (error) {
        console.error('Error fetching repositories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRepos();
  }, [approvedReposTrigger]); // Refresh when approvedReposTrigger changes

  // Whenever selection changes in the collection hook, update our local state
  useEffect(() => {
    setSelectedItems(collectionProps.selectedItems);
  }, [collectionProps.selectedItems]);

  const effectiveManagerList = getDelegationsFromRoles().map((del) => del.managerAlias);

  if (isAdmin()) {
    effectiveManagerList.push(user.userId);
  }

  const isManageableRepo = (repo) => {
    return effectiveManagerList.includes(repo.managerAlias);
  };

  // Function to refresh data
  const refreshData = async () => {
    setLoading(true);
    try {
      const response = await getRepos();
      setAllRepos(response.repos || []);
      actions.setSelectedItems([]);
    } catch (error) {
      console.error('Error refreshing repositories:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AddRepoModal isVisible={addModalIsVisible} setIsVisible={(isVisible) => setAddModalIsVisible(isVisible)} />
      <DeleteRepoModal
        isVisible={deleteModalIsVisible}
        setIsVisible={(isVisible) => setDeleteModalIsVisible(isVisible)}
        selectedRepos={selectedItems}
        setSelectedRepos={(repos) => actions.setSelectedItems(repos)}
      />

      <Table
        {...collectionProps}
        variant={isBypassesPhase1On() ? 'borderless' : undefined}
        selectionType={adminView ? 'multi' : undefined}
        columnDefinitions={columnDefinitions}
        visibleColumns={preferences.visibleContent}
        items={items}
        isItemDisabled={(repo) => !isManageableRepo(repo)}
        loading={loading}
        loadingText="Loading repositories..."
        trackBy={(repo) => `${repo.url}|${repo.managerAlias}`}
        resizableColumns
        stripedRows={preferences.stripedRows}
        contentDensity={preferences.contentDensity}
        wrapLines={preferences.wrapLines}
        preferences={
          <CollectionPreferences
            title="Repository preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={preferences}
            onConfirm={({ detail }) => onPreferencesChange({ detail })}
            pageSizePreference={{
              title: 'Page size',
              options: pageSizeOptions,
            }}
            visibleContentPreference={{
              title: 'Select visible columns',
              options: [
                {
                  label: 'Repository properties',
                  options: contentDisplayOptions,
                },
              ],
            }}
            wrapLinesPreference={{
              label: 'Wrap lines',
              description: 'Check to see all the text and wrap the lines',
            }}
            stripedRowsPreference={{
              label: 'Striped rows',
              description: 'Check to add alternating shaded rows',
            }}
            contentDensityPreference={{
              label: 'Compact mode',
              description: 'Display content in a denser, more compact mode',
            }}
          />
        }
        filter={
          <TextFilter
            data-testid="repo-search-filter"
            {...filterProps}
            filteringPlaceholder="Find repositories by URL, manager, or type"
            filteringAriaLabel="Filter repositories"
            countText={
              filteredItemsCount !== undefined
                ? `${filteredItemsCount} match${filteredItemsCount === 1 ? '' : 'es'}`
                : undefined
            }
            disabled={loading}
          />
        }
        header={
          <Header
            variant="awsui-h1-sticky"
            counter={
              selectedItems.length
                ? `(${selectedItems.length}/${filteredItemsCount})`
                : filteredItemsCount !== undefined
                  ? `(${filteredItemsCount})`
                  : undefined
            }
            actions={
              !adminView ? undefined : (
                <SpaceBetween direction={'horizontal'} size="xs">
                  <Button
                    data-testid={'delete-repo-button'}
                    onClick={() => {
                      setDeleteModalIsVisible(true);
                    }}
                    disabled={selectedItems.length === 0}
                  >
                    Delete
                  </Button>
                  <Button
                    data-testid={'add-repo-button'}
                    variant="primary"
                    onClick={() => {
                      setAddModalIsVisible(true);
                    }}
                    disabled={!isTopRopeFeatureOn()}
                  >
                    Add repository
                  </Button>
                </SpaceBetween>
              )
            }
          >
            Repositories
          </Header>
        }
        pagination={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button data-testid="refresh-btn" iconName="refresh" variant="icon" onClick={refreshData} />
              <Pagination {...paginationProps} />
            </SpaceBetween>
          </Box>
        }
      />
    </>
  );
};
